import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/UniversityDetails.css";
import harvardImg from "../assets/universities/hardvard.jpg";
import api, { API_ENDPOINTS } from "../config/api";
import { createSlug } from "../utils/pagination";
import { useAuth } from "../context/AuthContext";
import BackToTop from "../components/BackToTop";
import SEO from "./SEO";

const UniversityDetail = ({ universities = [] }) => {
  const { isLoggedIn } = useAuth();
  const { name } = useParams();
  const navigate = useNavigate();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const infoRef = useRef(null);
  const requirementsRef = useRef(null);
  const rankingsRef = useRef(null);
  const faqsRef = useRef(null);

  // Function to handle smooth scrolling to sections
  const scrollToSection = (ref) => {
    if (ref.current) {
      const element = ref.current;
      const headerHeight = 60; // Reduced header height
      const elementPosition = element.offsetTop - headerHeight - 10; // Reduced padding to 10px

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  // Helper function to transform university data
  const transformUniversityData = (rawData) => {
    if (!rawData) return null;

    // Helper function to clean data values
    const cleanValue = (value) => {
      if (value === null || value === undefined || value === "") return null;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (
          trimmed === "" ||
          trimmed.toLowerCase() === "na" ||
          trimmed.toLowerCase() === "n/a"
        ) {
          return null;
        }
        return trimmed;
      }
      return value;
    };

    // Helper function to parse numeric values and handle currency formatting
    const parseNumeric = (value) => {
      const cleaned = cleanValue(value);
      if (cleaned === null) {
        return null;
      }

      // Handle currency strings like "$50,000" or "50,000"
      if (typeof cleaned === "string") {
        const numericString = cleaned.replace(/[$,]/g, "");
        const parsed = parseFloat(numericString);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }

      const parsed = parseFloat(cleaned);
      if (isNaN(parsed)) {
        return null;
      }
      return parsed;
    };

    // Helper function to parse student count (handle comma-separated numbers)
    const parseStudentCount = (value) => {
      const cleaned = cleanValue(value);
      if (cleaned === null) {
        return null;
      }

      if (typeof cleaned === "string") {
        const numericString = cleaned.replace(/,/g, "");
        const parsed = parseInt(numericString, 10);
        if (!isNaN(parsed)) {
          return parsed;
        }
      }

      const parsed = parseInt(cleaned, 10);
      if (isNaN(parsed)) {
        return null;
      }
      return parsed;
    };

    // Transform the raw data to match our expected format
    return {
      name:
        cleanValue(rawData["University Name"]) ||
        cleanValue(rawData.name) ||
        "Unknown University",
      summary:
        cleanValue(rawData["Summary/About"]) ||
        cleanValue(rawData.summary) ||
        "No summary available",
      country: cleanValue(rawData["Country"]) || cleanValue(rawData.country),
      location: {
        city:
          cleanValue(rawData["City, State"]?.split(",")[0]) ||
          cleanValue(rawData.city),
        state:
          cleanValue(rawData["City, State"]?.split(",")[1]) ||
          cleanValue(rawData.state),
      },
      studentDemographics: {
        totalEnrolled:
          parseStudentCount(rawData["Total Enrolled"]) ||
          parseStudentCount(rawData["totalEnrolled"]) ||
          parseStudentCount(rawData["Student Count"]) ||
          parseStudentCount(rawData.totalEnrolled),
        internationalStudents:
          parseNumeric(rawData["International Students"]) ||
          parseNumeric(rawData["internationalStudents"]) ||
          parseNumeric(rawData["International Student %"]) ||
          parseNumeric(rawData.internationalStudents),
        maleFemaleRatio:
          cleanValue(rawData["Male percentage"]) &&
          cleanValue(rawData["Female Percentage"])
            ? `${cleanValue(rawData["Male percentage"])}% / ${cleanValue(
                rawData["Female Percentage"]
              )}%`
            : null,
        malePercentage: parseNumeric(rawData["Male percentage"]),
        femalePercentage: parseNumeric(rawData["Female Percentage"]),
        totalInternationalStudents: parseStudentCount(
          rawData["Total International Students"]
        ),
      },
      admissionRequirements: {
        averageGRE:
          parseNumeric(rawData["Average GRE score"]) ||
          parseNumeric(rawData.averageGRE),
        greVerbal:
          parseNumeric(rawData["Verbal Reasoning"]) ||
          parseNumeric(rawData.greVerbal),
        greQuantitative:
          parseNumeric(rawData["Quantitative Reasoning"]) ||
          parseNumeric(rawData.greQuantitative),
        greAnalyticalWriting:
          parseNumeric(rawData["GRE Analytical Writing"]) ||
          parseNumeric(rawData["Analytical Writing"]) ||
          parseNumeric(rawData.greAnalyticalWriting),
        averageTOEFL:
          parseNumeric(rawData["Average Toefl Score"]) ||
          parseNumeric(rawData.averageToefl),
        minTOEFL:
          parseNumeric(rawData["Minimum Toefl Score Required"]) ||
          parseNumeric(rawData.minToefl),
        averageIELTS:
          parseNumeric(rawData["Average Ielts Score"]) ||
          parseNumeric(rawData.averageIelts),
        minIELTS:
          parseNumeric(rawData["Minimum Ielts Score Required"]) ||
          parseNumeric(rawData.minIelts),
        averageGPA:
          parseNumeric(rawData["Average Gpa"]) ||
          parseNumeric(rawData.averageGpa),
        acceptanceRate:
          parseNumeric(rawData["Acceptance rate"]) ||
          parseNumeric(rawData.acceptanceRate),
        mastersAcceptanceRate:
          parseNumeric(rawData["Master's Acceptance Rate"]) ||
          parseNumeric(rawData.mastersAcceptanceRate),
        phdAcceptanceRate:
          parseNumeric(rawData["PhD Acceptance Rate"]) ||
          parseNumeric(rawData.phdAcceptanceRate),
        averageGMAT:
          parseNumeric(rawData["Average GMAT score"]) ||
          parseNumeric(rawData.averageGMAT),
        averageSAT:
          parseNumeric(rawData["Average SAT Score"]) ||
          parseNumeric(rawData.averageSAT),
      },
      financialInfo: {
        tuitionFees:
          parseNumeric(rawData["Tution Fees"]) ||
          parseNumeric(rawData["Tuition Fees"]) ||
          parseNumeric(rawData["tuitionFees"]) ||
          parseNumeric(rawData.tuitionFees),
        livingExpenses:
          parseNumeric(rawData["Living Expenses"]) ||
          parseNumeric(rawData["livingExpenses"]) ||
          parseNumeric(rawData["Living Cost"]) ||
          parseNumeric(rawData.livingExpenses),
        jugaadLivingExpenses:
          parseNumeric(rawData["Jugaad Living Expenses (USD) for one year"]) ||
          parseNumeric(rawData.jugaadLivingExpenses),
        applicationFeeMasters:
          parseNumeric(rawData["Application Fees for Masters"]) ||
          parseNumeric(rawData.applicationFeeMasters),
        applicationFeeUndergrad:
          parseNumeric(rawData["Application Fee for Undergrad"]) ||
          parseNumeric(rawData.applicationFeeUndergrad),
        financialAid:
          !!cleanValue(rawData["Financial Aid Link"]) ||
          !!cleanValue(rawData.financialAidLink),
        scholarships: true,
        fellowships: parseNumeric(rawData["Fellowships"]),
        teachingAssistantships: parseNumeric(
          rawData["Teaching Assistantships"]
        ),
        researchAssistantships: parseNumeric(
          rawData["Research Assistantships"]
        ),
        financialAidLink: cleanValue(rawData["Financial Aid Link"]),
        financialAidDirector: cleanValue(rawData["Financial Aid Director"]),
        financialAidPhoneNumber: cleanValue(
          rawData["Financial Aid Phone Number"]
        ),
      },
      careerOutcomes: {
        averageSalary:
          parseNumeric(rawData["Average Salary"]) ||
          parseNumeric(rawData.averageSalary),
        employmentRate:
          parseNumeric(rawData["Employed"]) ||
          parseNumeric(rawData.employmentRate),
        employedWithin3Months:
          parseNumeric(rawData["Employed within 3 months"]) ||
          parseNumeric(rawData.employedWithin3Months),
      },
      rankings: {
        usNews:
          parseNumeric(rawData["US News National rank"]) ||
          parseNumeric(rawData.usNewsRank),
        qsWorld:
          parseNumeric(rawData["QS World University Rankings"]) ||
          parseNumeric(rawData.qsWorldRank),
        timesHigher:
          parseNumeric(rawData["Times Higher Education Rank"]) ||
          parseNumeric(rawData.timesHigherRank),
        grad4gradRanking:
          parseNumeric(rawData["Grad4Grad Ranking"]) ||
          parseNumeric(rawData.grad4gradRanking),
      },
      contactInfo: {
        admissionsEmail: cleanValue(rawData["Admissions Email"]),
        universityWebsite:
          cleanValue(rawData["University Website Link"]) ||
          cleanValue(rawData["Univeristy Website Link"]),
        fullAddress: cleanValue(rawData["Full Address"]),
      },
      additionalInfo: {
        pros: cleanValue(rawData["Pros"]) || cleanValue(rawData.pros),
        cons: cleanValue(rawData["Cons"]) || cleanValue(rawData.cons),
        universityType: cleanValue(rawData["University Type"]),
      },
      faqs: [
        {
          question: "What is the acceptance rate?",
          answer: `The acceptance rate is ${
            parseNumeric(rawData["Acceptance rate"]) ||
            parseNumeric(rawData.acceptanceRate) ||
            "not available"
          }%.`,
        },
        {
          question: "What are the average GRE scores?",
          answer: `The average GRE score is ${
            parseNumeric(rawData["Average GRE score"]) ||
            parseNumeric(rawData.averageGRE) ||
            "not available"
          }, with Verbal Reasoning: ${
            parseNumeric(rawData["Verbal Reasoning"]) ||
            parseNumeric(rawData.greVerbal) ||
            "not available"
          } and Quantitative Reasoning: ${
            parseNumeric(rawData["Quantitative Reasoning"]) ||
            parseNumeric(rawData.greQuantitative) ||
            "not available"
          }.`,
        },
        {
          question: "Is financial aid available?",
          answer:
            cleanValue(rawData["Financial Aid Link"]) ||
            cleanValue(rawData.financialAidLink)
              ? `Yes, financial aid is available. You can find more information at: ${cleanValue(
                  rawData["Financial Aid Link"] || rawData.financialAidLink
                )}`
              : "Financial aid information is not available at this time.",
        },
        {
          question: "What are the pros and cons of joining this university?",
          answer: `Pros: ${
            cleanValue(rawData["Pros"]) ||
            cleanValue(rawData.pros) ||
            "Information not available"
          }.\n\nCons: ${
            cleanValue(rawData["Cons"]) ||
            cleanValue(rawData.cons) ||
            "Information not available"
          }.`,
        },
      ],
    };
  };

  // Fallback API call function
  const fetchUniversityDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use public or private endpoint based on authentication
      const endpoint = isLoggedIn
        ? `/api/universities/slug/${encodeURIComponent(name)}`
        : `/api/public/universities/slug/${encodeURIComponent(name)}`;
      const response = await api.get(endpoint);
      if (response.data) {
        const universityData = response.data.university || response.data;
        const transformedUniversity = transformUniversityData(universityData);
        setUniversity(transformedUniversity);
      } else {
        setError("University not found");
      }
    } catch (err) {
      console.error("❌ Error fetching university details:", err);
      setError("Failed to load university data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [name, isLoggedIn]);

  // Find university from the universities array passed as props
  useEffect(() => {
    if (!name) {
      setError("University not found");
      setLoading(false);
      return;
    }
    fetchUniversityDetails();
  }, [name, fetchUniversityDetails, isLoggedIn]);

  // Helper function to check if a value is valid (not null, undefined, empty, or 'NA')
  const isValid = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (
        trimmed === "" ||
        trimmed.toLowerCase() === "na" ||
        trimmed.toLowerCase() === "n/a"
      ) {
        return false;
      }
      return true;
    }
    if (typeof value === "number") {
      // For numeric values, check if they're valid numbers and not negative when they shouldn't be
      return !isNaN(value) && isFinite(value);
    }
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  const formatCurrency = (amount) => {
    if (
      amount === null ||
      amount === undefined ||
      isNaN(amount) ||
      amount <= 0
    ) {
      return "N/A";
    }

    // If it's already a string with currency formatting, return as is
    if (typeof amount === "string" && amount.includes("$")) {
      return amount;
    }

    // Convert to number and format
    const numericAmount =
      typeof amount === "string"
        ? parseFloat(amount.replace(/[$,]/g, ""))
        : amount;

    if (isNaN(numericAmount) || numericAmount <= 0) {
      return "N/A";
    }

    return `$${numericAmount.toLocaleString()}`;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h2>⚠️ Error Loading University Data</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button
              className="retry-button"
              onClick={() => {
                setError(null);
                setLoading(true);
                // Re-fetch the data from universities prop
                if (universities && universities.length > 0) {
                  const decodedName = decodeURIComponent(name);
                  const foundUniversity = universities.find((uni) => {
                    if (!uni.name) return false;

                    // Try exact name match (case-insensitive)
                    if (uni.name.toLowerCase() === decodedName.toLowerCase()) {
                      return true;
                    }

                    // Try slug match
                    if (createSlug(uni.name) === decodedName) {
                      return true;
                    }

                    // Try slug match with city and state
                    const fullSlug = createSlug(
                      uni.name +
                        " " +
                        (uni.city || "") +
                        " " +
                        (uni.state || "")
                    );
                    if (fullSlug === decodedName) {
                      return true;
                    }

                    return false;
                  });

                  if (foundUniversity) {
                    const transformedUniversity =
                      transformUniversityData(foundUniversity);
                    setUniversity(transformedUniversity);
                    setError(null);
                  } else {
                    setError("University not found");
                  }
                } else {
                  setError("No university data available");
                }
                setLoading(false);
              }}
            >
              🔄 Try Again
            </button>
            <button className="back-button" onClick={() => navigate(-1)}>
              &larr; Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!university) {
    return (
      <div className="university-not-found">
        <h2>University not found</h2>
        <p>We couldn't find the university you're looking for.</p>
        <button className="back-button" onClick={() => navigate(-1)}>
          &larr; Go Back
        </button>
      </div>
    );
  }

  // Back button handler
  const handleBackClick = () => {
    navigate(-1);
  };

  // Function to handle application guidance button click
  const handleApplicationGuidance = () => {
    window.open("https://shorturl.at/uvKtH", "_blank");
  };

  // Function to handle rate my chances button click
  const handleRateMyChances = () => {
    if (!isLoggedIn) {
      navigate("/login");
    } else {
      navigate("/rate-my-chances");
    }
  };

  // Add the renderOverview function
  const renderOverview = () => (
    <div className="details-section" ref={overviewRef}>
      <h2 className="section-title">Overview</h2>
      <p>{university.summary || "No overview available."}</p>

      {/* Pros and Cons Section - Only for logged in users */}
      {isLoggedIn ? (
        <div className="pros-cons-section">
          <div className="pros-section">
            <h3 className="pros-title">Pros of Joining {university.name}</h3>
            <div className="pros-content">
              {university.faqs &&
              university.faqs.length > 0 &&
              university.faqs.find((faq) =>
                faq.question.includes("pros and cons")
              )?.answer ? (
                <div style={{ whiteSpace: "pre-line" }}>
                  {
                    university.faqs
                      .find((faq) => faq.question.includes("pros and cons"))
                      ?.answer.split("\n\n")[0]
                  }
                </div>
              ) : (
                <p>Information not available</p>
              )}
            </div>
          </div>

          <div className="cons-section">
            <h3 className="cons-title">Cons of Joining {university.name}</h3>
            <div className="cons-content">
              {university.faqs &&
              university.faqs.length > 0 &&
              university.faqs.find((faq) =>
                faq.question.includes("pros and cons")
              )?.answer ? (
                <div style={{ whiteSpace: "pre-line" }}>
                  {
                    university.faqs
                      .find((faq) => faq.question.includes("pros and cons"))
                      ?.answer.split("\n\n")[1]
                  }
                </div>
              ) : (
                <p>Information not available</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="login-required-section">
          <div className="login-required-message">
            <h3>🔒 Login Required</h3>
            <p>
              Please log in to view detailed pros and cons analysis for this
              university.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Login to View Analysis
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // University Information Section (new schema)
  const renderUniversityInfo = () => (
    <div className="details-section" ref={infoRef}>
      <h2 className="section-title">University Information</h2>

      {!isLoggedIn ? (
        <div className="login-required-section">
          <div className="login-required-message">
            <h3>🔒 Login Required</h3>
            <p>
              Please log in to view detailed university information including
              financial details, student demographics, and career outcomes.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Login to View Details
            </button>
          </div>
        </div>
      ) : (
        <div className="university-info-grid">
          {/* Basic Information */}
          <div className="info-section">
            <h3 className="info-section-title">Basic Information</h3>
            <table className="university-details-table">
              <tbody>
                {isValid(university.country) && (
                  <tr>
                    <th>Country</th>
                    <td>{university.country}</td>
                  </tr>
                )}
                {isValid(university.location?.city) && (
                  <tr>
                    <th>City</th>
                    <td>{university.location.city}</td>
                  </tr>
                )}
                {isValid(university.location?.state) && (
                  <tr>
                    <th>State</th>
                    <td>{university.location.state}</td>
                  </tr>
                )}
                {isValid(university.contactInfo?.fullAddress) && (
                  <tr>
                    <th>Full Address</th>
                    <td>{university.contactInfo.fullAddress}</td>
                  </tr>
                )}
                {isValid(university.additionalInfo?.universityType) && (
                  <tr>
                    <th>University Type</th>
                    <td>{university.additionalInfo.universityType}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Student Demographics */}
          <div className="info-section">
            <h3 className="info-section-title">Student Demographics</h3>
            <table className="university-details-table">
              <tbody>
                {isValid(university.studentDemographics?.totalEnrolled) && (
                  <tr>
                    <th>Total Enrolled Students</th>
                    <td>
                      {university.studentDemographics.totalEnrolled.toLocaleString()}
                    </td>
                  </tr>
                )}
                {isValid(
                  university.studentDemographics?.internationalStudents
                ) && (
                  <tr>
                    <th>International Students</th>
                    <td>
                      {university.studentDemographics.internationalStudents}%
                    </td>
                  </tr>
                )}
                {isValid(
                  university.studentDemographics?.totalInternationalStudents
                ) && (
                  <tr>
                    <th>Total International Students</th>
                    <td>
                      {university.studentDemographics.totalInternationalStudents.toLocaleString()}
                    </td>
                  </tr>
                )}
                {isValid(university.studentDemographics?.malePercentage) && (
                  <tr>
                    <th>Male Percentage</th>
                    <td>{university.studentDemographics.malePercentage}%</td>
                  </tr>
                )}
                {isValid(university.studentDemographics?.femalePercentage) && (
                  <tr>
                    <th>Female Percentage</th>
                    <td>{university.studentDemographics.femalePercentage}%</td>
                  </tr>
                )}
                {isValid(university.studentDemographics?.maleFemaleRatio) && (
                  <tr>
                    <th>Male/Female Ratio</th>
                    <td>{university.studentDemographics.maleFemaleRatio}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Financial Information */}
          <div className="info-section">
            <h3 className="info-section-title">Financial Information</h3>
            <table className="university-details-table">
              <tbody>
                {isValid(university.financialInfo?.tuitionFees) && (
                  <tr>
                    <th>Tuition Fees</th>
                    <td>
                      {formatCurrency(university.financialInfo.tuitionFees)}
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.livingExpenses) && (
                  <tr>
                    <th>Living Expenses</th>
                    <td>
                      {formatCurrency(university.financialInfo.livingExpenses)}
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.jugaadLivingExpenses) && (
                  <tr>
                    <th>Jugaad Living Expenses</th>
                    <td>
                      {formatCurrency(
                        university.financialInfo.jugaadLivingExpenses
                      )}
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.applicationFeeMasters) && (
                  <tr>
                    <th>Application Fee (Masters)</th>
                    <td>
                      {formatCurrency(
                        university.financialInfo.applicationFeeMasters
                      )}
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.applicationFeeUndergrad) && (
                  <tr>
                    <th>Application Fee (Undergraduate)</th>
                    <td>
                      {formatCurrency(
                        university.financialInfo.applicationFeeUndergrad
                      )}
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.fellowships) && (
                  <tr>
                    <th>Fellowships Available</th>
                    <td>{university.financialInfo.fellowships}</td>
                  </tr>
                )}
                {isValid(university.financialInfo?.teachingAssistantships) && (
                  <tr>
                    <th>Teaching Assistantships</th>
                    <td>{university.financialInfo.teachingAssistantships}</td>
                  </tr>
                )}
                {isValid(university.financialInfo?.researchAssistantships) && (
                  <tr>
                    <th>Research Assistantships</th>
                    <td>{university.financialInfo.researchAssistantships}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Career Outcomes */}
          <div className="info-section">
            <h3 className="info-section-title">Career Outcomes</h3>
            <table className="university-details-table">
              <tbody>
                {isValid(university.careerOutcomes?.averageSalary) && (
                  <tr>
                    <th>Average Salary After Graduation</th>
                    <td>
                      {formatCurrency(university.careerOutcomes.averageSalary)}
                    </td>
                  </tr>
                )}
                {isValid(university.careerOutcomes?.employmentRate) && (
                  <tr>
                    <th>Employment Rate</th>
                    <td>{university.careerOutcomes.employmentRate}%</td>
                  </tr>
                )}
                {isValid(university.careerOutcomes?.employedWithin3Months) && (
                  <tr>
                    <th>Employed Within 3 Months</th>
                    <td>{university.careerOutcomes.employedWithin3Months}%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Contact Information */}
          <div className="info-section">
            <h3 className="info-section-title">Contact Information</h3>
            <table className="university-details-table">
              <tbody>
                {isValid(university.contactInfo?.admissionsEmail) && (
                  <tr>
                    <th>Admissions Email</th>
                    <td>
                      <div className="contact-item">
                        <a
                          href={`mailto:${university.contactInfo.admissionsEmail}`}
                          className="contact-link email-link"
                        >
                          {university.contactInfo.admissionsEmail}
                        </a>
                        <button
                          className="copy-button"
                          onClick={(event) => {
                            navigator.clipboard.writeText(
                              university.contactInfo.admissionsEmail
                            );
                            // Show feedback
                            const button = event.target;
                            const originalText = button.innerHTML;
                            button.innerHTML = "✓ Copied!";
                            button.classList.add("copied");
                            setTimeout(() => {
                              button.innerHTML = originalText;
                              button.classList.remove("copied");
                            }, 2000);
                          }}
                          title="Copy email address"
                        >
                          <span className="copy-icon">📋</span>
                          <span className="copy-text">Copy</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {isValid(university.contactInfo?.universityWebsite) && (
                  <tr>
                    <th>University Website</th>
                    <td>
                      <a
                        href={university.contactInfo.universityWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-link website-link"
                      >
                        Visit Website
                      </a>
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.financialAidLink) && (
                  <tr>
                    <th>Financial Aid</th>
                    <td>
                      <a
                        href={university.financialInfo.financialAidLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-link financial-aid-link"
                      >
                        Financial Aid Information
                      </a>
                    </td>
                  </tr>
                )}
                {isValid(university.financialInfo?.financialAidDirector) && (
                  <tr>
                    <th>Financial Aid Director</th>
                    <td>{university.financialInfo.financialAidDirector}</td>
                  </tr>
                )}
                {isValid(university.financialInfo?.financialAidPhoneNumber) && (
                  <tr>
                    <th>Financial Aid Phone</th>
                    <td>
                      <div className="contact-item">
                        <a
                          href={`tel:${university.financialInfo.financialAidPhoneNumber}`}
                          className="contact-link phone-link"
                        >
                          {university.financialInfo.financialAidPhoneNumber}
                        </a>
                        <button
                          className="copy-button"
                          onClick={(event) => {
                            navigator.clipboard.writeText(
                              university.financialInfo.financialAidPhoneNumber
                            );
                            // Show feedback
                            const button = event.target;
                            const originalText = button.innerHTML;
                            button.innerHTML = "✓ Copied!";
                            button.classList.add("copied");
                            setTimeout(() => {
                              button.innerHTML = originalText;
                              button.classList.remove("copied");
                            }, 2000);
                          }}
                          title="Copy phone number"
                        >
                          <span className="copy-icon">📋</span>
                          <span className="copy-text">Copy</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Admission Requirements Section (new schema)
  const renderAdmissionRequirements = () => (
    <div className="details-section" ref={requirementsRef}>
      <h2 className="section-title">Admission Requirements</h2>

      {!isLoggedIn ? (
        <div className="login-required-section">
          <div className="login-required-message">
            <h3>🔒 Login Required</h3>
            <p>
              Please log in to view detailed admission requirements including
              GRE scores, GPA requirements, and language test scores.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Login to View Requirements
            </button>
          </div>
        </div>
      ) : (
        <div className="requirements-grid">
          {/* GRE Scores */}
          <div className="requirements-section">
            <h3 className="requirements-section-title">GRE Scores</h3>
            <table className="university-details-table requirements-table">
              <tbody>
                {isValid(university.admissionRequirements?.averageGRE) && (
                  <tr>
                    <th>Average GRE</th>
                    <td>{university.admissionRequirements.averageGRE}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.greVerbal) && (
                  <tr>
                    <th>GRE Verbal Reasoning</th>
                    <td>{university.admissionRequirements.greVerbal}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.greQuantitative) && (
                  <tr>
                    <th>GRE Quantitative Reasoning</th>
                    <td>{university.admissionRequirements.greQuantitative}</td>
                  </tr>
                )}
                {isValid(
                  university.admissionRequirements?.greAnalyticalWriting
                ) && (
                  <tr>
                    <th>GRE Analytical Writing</th>
                    <td>
                      {university.admissionRequirements.greAnalyticalWriting}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Language Test Scores */}
          <div className="requirements-section">
            <h3 className="requirements-section-title">Language Test Scores</h3>
            <table className="university-details-table requirements-table">
              <tbody>
                {isValid(university.admissionRequirements?.averageTOEFL) && (
                  <tr>
                    <th>Average TOEFL Score</th>
                    <td>{university.admissionRequirements.averageTOEFL}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.minTOEFL) && (
                  <tr>
                    <th>Minimum TOEFL Score</th>
                    <td>{university.admissionRequirements.minTOEFL}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.averageIELTS) && (
                  <tr>
                    <th>Average IELTS Score</th>
                    <td>{university.admissionRequirements.averageIELTS}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.minIELTS) && (
                  <tr>
                    <th>Minimum IELTS Score</th>
                    <td>{university.admissionRequirements.minIELTS}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Academic Requirements */}
          <div className="requirements-section">
            <h3 className="requirements-section-title">
              Academic Requirements
            </h3>
            <table className="university-details-table requirements-table">
              <tbody>
                {isValid(university.admissionRequirements?.averageGPA) && (
                  <tr>
                    <th>Average GPA</th>
                    <td>{university.admissionRequirements.averageGPA}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.averageGMAT) && (
                  <tr>
                    <th>Average GMAT Score</th>
                    <td>{university.admissionRequirements.averageGMAT}</td>
                  </tr>
                )}
                {isValid(university.admissionRequirements?.averageSAT) && (
                  <tr>
                    <th>Average SAT Score</th>
                    <td>{university.admissionRequirements.averageSAT}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Acceptance Rates */}
          <div className="requirements-section">
            <h3 className="requirements-section-title">Acceptance Rates</h3>
            <table className="university-details-table requirements-table">
              <tbody>
                {isValid(university.admissionRequirements?.acceptanceRate) && (
                  <tr>
                    <th>Overall Acceptance Rate</th>
                    <td>{university.admissionRequirements.acceptanceRate}%</td>
                  </tr>
                )}
                {isValid(
                  university.admissionRequirements?.mastersAcceptanceRate
                ) && (
                  <tr>
                    <th>Master's Acceptance Rate</th>
                    <td>
                      {university.admissionRequirements.mastersAcceptanceRate}%
                    </td>
                  </tr>
                )}
                {isValid(
                  university.admissionRequirements?.phdAcceptanceRate
                ) && (
                  <tr>
                    <th>PhD Acceptance Rate</th>
                    <td>
                      {university.admissionRequirements.phdAcceptanceRate}%
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Rankings Section (new schema)
  const renderRankings = () => (
    <div className="details-section" ref={rankingsRef}>
      <h2 className="section-title">Rankings</h2>

      {!isLoggedIn ? (
        <div className="login-required-section">
          <div className="login-required-message">
            <h3>🔒 Login Required</h3>
            <p>
              Please log in to view university rankings and performance metrics.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Login to View Rankings
            </button>
          </div>
        </div>
      ) : (
        <table className="university-details-table">
          <tbody>
            {isValid(university.rankings?.usNews) && (
              <tr>
                <th>US News National Rank</th>
                <td>#{university.rankings.usNews}</td>
              </tr>
            )}
            {isValid(university.rankings?.qsWorld) && (
              <tr>
                <th>QS World University Rankings</th>
                <td>#{university.rankings.qsWorld}</td>
              </tr>
            )}
            {isValid(university.rankings?.timesHigher) && (
              <tr>
                <th>Times Higher Education Rank</th>
                <td>#{university.rankings.timesHigher}</td>
              </tr>
            )}
            {isValid(university.rankings?.grad4gradRanking) && (
              <tr>
                <th>Grad4Grad Ranking</th>
                <td>#{university.rankings.grad4gradRanking}</td>
              </tr>
            )}
            {isValid(university.rankings?.arwu) && (
              <tr>
                <th>ARWU</th>
                <td>#{university.rankings.arwu}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );

  // FAQs Section (new schema)
  const renderFAQs = () => (
    <div className="details-section" ref={faqsRef}>
      <h2 className="section-title">FAQs</h2>

      {!isLoggedIn ? (
        <div className="login-required-section">
          <div className="login-required-message">
            <h3>🔒 Login Required</h3>
            <p>
              Please log in to view frequently asked questions and detailed
              answers about this university.
            </p>
            <button className="login-btn" onClick={() => navigate("/login")}>
              Login to View FAQs
            </button>
          </div>
        </div>
      ) : (
        <div className="faqs-list">
          {Array.isArray(university.faqs) &&
            university.faqs
              .filter((faq) => isValid(faq.question) && isValid(faq.answer))
              .map((faq, idx) => {
                // Special handling for the financial aid FAQ
                if (faq.question.toLowerCase().includes("financial aid")) {
                  // Try to extract a URL from the answer
                  const urlMatch = faq.answer.match(/https?:\/\/[^\s)]+/);
                  if (urlMatch) {
                    const url = urlMatch[0];
                    // Split the answer into before/after the URL
                    const [before, after] = faq.answer.split(url);
                    return (
                      <div className="faq-item" key={idx}>
                        <strong>Q: {faq.question}</strong>
                        <div className="faq-answer">
                          {before && <p>{before}</p>}
                          <div className="faq-link-container">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="faq-link"
                            >
                              Visit Financial Aid Page
                            </a>
                          </div>
                          {after && <p>{after}</p>}
                        </div>
                      </div>
                    );
                  }
                }
                // Default rendering for other FAQs
                return (
                  <div className="faq-item" key={idx}>
                    <strong>Q: {faq.question}</strong>
                    <div className="faq-answer">
                      <p>A: {faq.answer}</p>
                    </div>
                  </div>
                );
              })}
        </div>
      )}
    </div>
  );

  return (
    <div className="university-details-page">
      <SEO
        title={`${
          university?.name || "University"
        } - Complete Guide for International Students | Grad4Grad`}
        description={`Comprehensive guide to ${
          university?.name || "this university"
        } for international students. Get detailed information on admission requirements, tuition fees (${
          university?.financialInfo?.tuitionFees
            ? formatCurrency(university.financialInfo.tuitionFees)
            : "available"
        }), acceptance rate (${
          university?.admissionRequirements?.acceptanceRate
            ? university.admissionRequirements.acceptanceRate + "%"
            : "available"
        }), student demographics, and career outcomes. Expert guidance for study abroad applications.`}
        keywords={`${
          university?.name || "university"
        }, study abroad, international students, admission requirements, tuition fees, acceptance rate, student demographics, career outcomes, graduate school, masters degree, PhD programs, ${
          university?.country || "study abroad"
        }, ${university?.location?.city || "university city"}`}
        image={harvardImg}
        url={`${window.location.origin}/university/${createSlug(
          university?.name || "university"
        )}`}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          name: university?.name || "University",
          description:
            university?.summary ||
            "University information for international students",
          url: `${window.location.origin}/university/${createSlug(
            university?.name || "university"
          )}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: university?.location?.city || "",
            addressRegion: university?.location?.state || "",
            addressCountry: university?.country || "",
          },
          numberOfStudents:
            university?.studentDemographics?.totalEnrolled || null,
          foundingDate: "1800", // Generic founding date
          sameAs: [
            "https://grad4grad.com",
            "https://grad4grad.com/universities",
          ],
          potentialAction: {
            "@type": "ApplyAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://grad4grad.com/rate-my-chances",
            },
            description: "Apply for admission guidance",
          },
        }}
      />
      <div className="university-hero">
        <img
          src={harvardImg}
          alt={university.name}
          className="university-banner"
          onError={() => setImageError(true)}
          style={imageError ? { opacity: 0.5 } : {}}
        />
        <div className="university-overlay">
          <h1 className="university-title">{university.name}</h1>
          <p className="university-location">
            {university.location?.city ? university.location.city : ""}
            {university.location?.city && university.location?.state
              ? ", "
              : ""}
            {university.location?.state ? university.location.state : ""}
          </p>
        </div>
      </div>

      <div className="university-details-container">
        <button className="back-button" onClick={handleBackClick}>
          &larr; Back to Universities
        </button>

        <div className="university-overview">
          <div className="university-quick-facts">
            <div className="fact-item">
              <span className="fact-label">Acceptance Rate</span>
              <span className="fact-value">
                {university.admissionRequirements?.acceptanceRate
                  ? `${university.admissionRequirements.acceptanceRate}%`
                  : "N/A"}
              </span>
            </div>
            <div className="fact-item">
              <span className="fact-label">Tuition</span>
              <span className="fact-value">
                {formatCurrency(university.financialInfo?.tuitionFees)}
              </span>
            </div>
            <div className="fact-item">
              <span className="fact-label">Living Expenses</span>
              <span className="fact-value">
                {formatCurrency(university.financialInfo?.livingExpenses)}
              </span>
            </div>
            <div className="fact-item">
              <span className="fact-label">Students</span>
              <span className="fact-value">
                {university.studentDemographics?.totalEnrolled
                  ? university.studentDemographics.totalEnrolled.toLocaleString()
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>

        <div className="university-tabs">
          <div className="tab" onClick={() => scrollToSection(overviewRef)}>
            Overview
          </div>
          <div className="tab" onClick={() => scrollToSection(infoRef)}>
            University Information
          </div>
          <div className="tab" onClick={() => scrollToSection(requirementsRef)}>
            Admission Requirements
          </div>
          <div className="tab" onClick={() => scrollToSection(rankingsRef)}>
            Rankings
          </div>
          <div className="tab" onClick={() => scrollToSection(faqsRef)}>
            FAQs
          </div>
        </div>

        <div className="university-details-content">
          {renderOverview()}
          {renderUniversityInfo()}
          {renderAdmissionRequirements()}
          {renderRankings()}
          {renderFAQs()}
        </div>

        <div className="university-cta-section">
          <h2>Ready to apply to {university.name}?</h2>
          <p>Let our experts help you with the application process</p>
          <div className="cta-buttons">
            <button
              className="cta-button primary"
              onClick={handleApplicationGuidance}
            >
              Get Free Application Guidance
            </button>
            <button
              className="cta-button secondary"
              onClick={handleRateMyChances}
            >
              Rate My Chances
            </button>
          </div>
        </div>
      </div>
      <BackToTop />
    </div>
  );
};

export default UniversityDetail;
